/**
 * CategorizeSongPopover — v3.0.141
 * Multi-select checklist of user categories for tagging a single song.
 * Uses POST /songs/{id}/categories to replace the whole array (simpler
 * than tracking diffs). Optimistic UI so toggles feel instant.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check, Plus } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { apiFetch } from '../App';

export default function CategorizeSongPopover({
  songId, currentIds = [], trigger, onManage, onChange,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set(currentIds));

  useEffect(() => { setSelected(new Set(currentIds || [])); }, [currentIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/categories');
      setCategories(Array.isArray(data?.custom) ? data.custom : []);
    } catch (err) {
      toast.error(`Load failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const commit = async () => {
    setSaving(true);
    try {
      const arr = Array.from(selected);
      const { data } = await apiFetch(`/songs/${songId}/categories`, {
        method: 'POST',
        body: JSON.stringify({ categories: arr }),
      });
      if (onChange) onChange(data?.categories || arr);
      toast.success('Categories updated');
      setOpen(false);
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-1 bg-zinc-950 border-white/10 text-white"
        data-testid="categorize-song-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-zinc-500">
          Tag this song
        </div>
        <div className="max-h-[45vh] overflow-y-auto">
          {loading ? (
            <div className="py-4 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : categories.length === 0 ? (
            <div className="px-3 py-4 text-xs text-zinc-500 text-center">
              No custom categories yet.
            </div>
          ) : (
            categories.map((c) => {
              const on = selected.has(c.category_id);
              return (
                <button
                  key={c.category_id}
                  onClick={() => toggle(c.category_id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm text-left transition-colors ${
                    on ? 'bg-emerald-500/15 text-emerald-100' : 'text-zinc-300 hover:bg-white/5'
                  }`}
                  data-testid={`categorize-toggle-${c.category_id}`}
                >
                  <span className="w-4 flex justify-center text-base leading-none">
                    {c.emoji || '📌'}
                  </span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {on && <Check className="h-3.5 w-3.5 text-emerald-400" />}
                </button>
              );
            })
          )}
        </div>
        <div className="h-px bg-white/10 my-1" />
        <div className="flex gap-1 p-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setOpen(false); if (onManage) onManage(); }}
            className="flex-1 text-zinc-300 hover:bg-white/5 justify-start"
            data-testid="categorize-manage-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            New / Manage…
          </Button>
          <Button
            size="sm"
            onClick={commit}
            disabled={saving}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="categorize-save-btn"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
